<template>
  <div class="chat-history-page">
    <div class="chat-history-header">
      <div>
        <div class="chat-history-kicker">
          History
        </div>

        <h2>Chat History / Sessions</h2>
      </div>

      <v-btn
        color="primary"
        depressed
        :loading="loading"
        @click="fetchHistory"
      >
        <v-icon left small>
          mdi-refresh
        </v-icon>
        Refresh
      </v-btn>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <div class="chat-history-source-tabs mb-4">
      <v-tabs
        v-model="activeSourceTab"
        show-arrows
        background-color="transparent"
        color="primary"
      >
        <v-tab>
          <v-icon small class="mr-1">
            mdi-format-list-bulleted
          </v-icon>
          All Sources
        </v-tab>

        <v-tab
          v-for="source in sources"
          :key="source"
        >
          <v-icon small class="mr-1">
            mdi-web
          </v-icon>
          {{ source }}
        </v-tab>
      </v-tabs>
    </div>

    <div class="chat-history-filter-info mb-4">
      <v-icon small color="primary" class="mr-2">
        mdi-filter-outline
      </v-icon>

      <template v-if="selectedSource">
        Current Source Filter: <strong>{{ selectedSource }}</strong>
      </template>

      <template v-else>
        All sources are visible.
      </template>
    </div>

    <v-card outlined class="chat-history-card">
      <v-data-table
        :headers="headers"
        :items="sessions"
        :loading="loading"
        item-key="id"
        class="elevation-0 chat-history-table"
        :items-per-page="15"
        @click:row="openSession"
      >
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="getStatusColor(item.status)"
            text-color="white"
            class="font-weight-bold"
          >
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.customer="{ item }">
          <div class="chat-history-customer-cell">
            <span class="chat-history-avatar">
              {{ getCustomerInitials(item) }}
            </span>

            <span class="chat-history-customer-main">
              <span class="chat-history-customer-name">
                {{ item.customer_name || 'Névtelen látogató' }}

                <v-chip
                  v-if="item.unread_count > 0"
                  x-small
                  color="red"
                  text-color="white"
                  class="ml-1"
                >
                  {{ item.unread_count }}
                </v-chip>
              </span>

              <span class="chat-history-customer-contact">
                {{ item.customer_email || '-' }}
                <template v-if="item.customer_phone">
                  · {{ item.customer_phone }}
                </template>
              </span>
            </span>
          </div>
        </template>

        <template v-slot:item.source="{ item }">
          <div class="chat-history-source-cell">
            <v-chip
              x-small
              color="blue-grey"
              text-color="white"
              class="mb-1"
            >
              {{ item.source_site || '-' }}
            </v-chip>

            <div class="caption text--secondary">
              {{ item.locale || '-' }}
            </div>
          </div>
        </template>

        <template v-slot:item.last_message="{ item }">
          <span class="chat-last-message">
            {{ getLastMessageText(item) }}
          </span>
        </template>

        <template v-slot:item.last_message_at="{ item }">
          <span class="chat-history-date">
            {{ formatDate(item.last_message_at || item.created_at) }}
          </span>
        </template>

        <template v-slot:item.operator="{ item }">
          <span class="chat-history-operator">
            {{ item.operator_display_name || '-' }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="980"
      scrollable
    >
      <v-card
        v-if="selectedSession"
        class="chat-history-modal"
      >
        <div class="chat-history-modal-header">
          <div class="chat-history-modal-main">
            <div class="chat-history-avatar chat-history-avatar-large">
              {{ getCustomerInitials(selectedSession) }}
            </div>

            <div>
              <div class="chat-history-modal-title">
                Chat #{{ selectedSession.id }} · {{ selectedSession.customer_name || 'Névtelen látogató' }}
              </div>

              <div class="chat-history-modal-subtitle">
                {{ selectedSession.source_site || '-' }} · {{ selectedSession.locale || '-' }} · {{ formatDate(selectedSession.created_at) }}
              </div>
            </div>
          </div>

          <v-chip
            small
            :color="getStatusColor(selectedSession.status)"
            text-color="white"
            class="font-weight-bold"
          >
            {{ getStatusLabel(selectedSession.status) }}
          </v-chip>
        </div>

        <v-divider />

        <v-card-text class="chat-history-modal-body">
          <v-row class="mb-4 chat-history-details">
            <v-col cols="12" md="6">
              <div class="chat-history-detail-card">
                <div class="chat-history-detail-title">
                  Customer Details
                </div>

                <div class="chat-history-detail-row">
                  <span>Name</span>
                  <strong>{{ selectedSession.customer_name || 'Unnamed Visitor' }}</strong>
                </div>

                <div class="chat-history-detail-row">
                  <span>E-mail</span>
                  <strong>{{ selectedSession.customer_email || '-' }}</strong>
                </div>

                <div class="chat-history-detail-row">
                  <span>Phone</span>
                  <strong>{{ selectedSession.customer_phone || '-' }}</strong>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="chat-history-detail-card">
                <div class="chat-history-detail-title">
                  Source Information
                </div>

                <div class="chat-history-detail-row">
                  <span>Source</span>
                  <strong>{{ selectedSession.source_site || '-' }}</strong>
                </div>

                <div class="chat-history-detail-row chat-history-url-row">
                  <span>URL</span>
                  <strong>{{ selectedSession.page_url || '-' }}</strong>
                </div>

                <div class="chat-history-detail-row">
                  <span>Operator</span>
                  <strong>{{ selectedSession.operator_display_name || '-' }}</strong>
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="history-chat-messages">
            <div
              v-for="message in selectedSession.messages"
              :key="message.id"
              class="history-chat-message-row"
              :class="{
                'history-chat-message-row-operator': message.sender_type === 'operator',
                'history-chat-message-row-system': message.sender_type === 'system'
              }"
            >
              <div
                class="history-chat-bubble"
                :class="{
                  'history-chat-bubble-customer': message.sender_type === 'customer',
                  'history-chat-bubble-operator': message.sender_type === 'operator',
                  'history-chat-bubble-system': message.sender_type === 'system'
                }"
              >
                <div class="history-chat-message-meta">
                  {{ getSenderLabel(message.sender_type) }} · {{ formatDate(message.created_at) }}
                </div>

                <div class="history-chat-message-text">
                  {{ message.display_message || message.message }}
                </div>
              </div>
            </div>

            <div
              v-if="!selectedSession.messages || !selectedSession.messages.length"
              class="chat-history-empty-messages"
            >
              There are no messages for this chat.
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import moment from "moment";
import ChatService from "@/api/chat.service";

export default {
  name: "ChatHistoryList",

  props: {
    sourceFilter: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      errorMessage: null,
      sessions: [],
      dialog: false,
      selectedSession: null,
      headers: [
        {
          text: "Status",
          value: "status",
          width: 120,
        },
        {
          text: "Customer",
          value: "customer",
        },
        {
          text: "Source",
          value: "source",
        },
        {
          text: "Last Message",
          value: "last_message",
        },
        {
          text: "Operator",
          value: "operator",
          width: 140,
        },
        {
          text: "Last Message Time",
          value: "last_message_at",
          width: 150,
        },
      ],

      activeSourceTab: 0,
      sources: [],
    };
  },

  mounted() {
    this.fetchSources();
    this.fetchHistory();
  },

  watch: {
    sourceFilter() {
      this.fetchHistory();
    },
    
    activeSourceTab() {
      this.fetchHistory();
    },
  },

  computed: {
    selectedSource() {
      if (this.activeSourceTab === 0) {
        return null;
      }

      return this.sources[this.activeSourceTab - 1] || null;
    },
  },

  methods: {
    async fetchHistory() {
      this.loading = true;
      this.errorMessage = null;
        
      try {
        const response = await ChatService.getHistorySessions(this.getHistoryParams());
        this.sessions = response.data.sessions || [];
      } catch (error) {
        this.errorMessage = "Unable to retrieve the chat history.";
      } finally {
        this.loading = false;
      }
    },

    async openSession(item) {
      this.loading = true;
        this.errorMessage = null;
  
      try {
          const response = await ChatService.getSession(item.id);
  
        this.selectedSession = response.data.session;
          this.dialog = true;
  
          await this.markHistorySessionRead(item.id);
  
        this.$nextTick(() => {
          this.scrollHistoryChatToBottom();
        });
      } catch (error) {
        this.errorMessage = "Unable to open the chat content.";
      } finally {
        this.loading = false;
      }
    },


    async markHistorySessionRead(id) {
      if (!this.selectedSession || !this.selectedSession.status) {
        return;
      }
    
      if (this.selectedSession.status === "waiting") {
        return;
      }
    
      try {
        await ChatService.markHistorySessionRead(id);
      
        if (this.selectedSession.messages) {
          this.selectedSession.messages = this.selectedSession.messages.map((message) => {
            if (message.sender_type === "customer") {
              return {
                ...message,
                is_read: true,
                read_at: new Date().toISOString(),
              };
            }
          
            return message;
          });
        }
      
        this.sessions = this.sessions.map((session) => {
          if (parseInt(session.id, 10) === parseInt(id, 10)) {
            return {
              ...session,
              unread_count: 0,
            };
          }
        
          return session;
        });
      
        window.dispatchEvent(new CustomEvent("chat-summary-refresh"));
      } catch (error) {
        console.error("History mark read failed", error);
      }
    },

    scrollHistoryChatToBottom() {
      const el = this.$el.querySelector(".history-chat-messages");
    
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    getCustomerInitials(item) {
      const name = item && item.customer_name ? item.customer_name : "";

      if (!name) {
        return "V";
      }

      const parts = name.trim().split(/\s+/);

      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }

      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    },

    getLastMessageText(item) {
      if (!item.last_message) {
        return "-";
      }

      return item.last_message.display_message || item.last_message.message;
    },

    getSenderLabel(senderType) {
      if (senderType === "customer") {
        return "Customer";
      }

      if (senderType === "operator") {
        return "Operator";
      }

      if (senderType === "system") {
        return "System";
      }

      return senderType;
    },

    getStatusLabel(status) {
      const labels = {
        waiting: "Waiting",
        active: "Active",
        offline_form: "Offline message",
        missed: "Missed",
        closed: "Closed",
      };

      return labels[status] || status;
    },

    getStatusColor(status) {
      const colors = {
        waiting: "orange",
        active: "success",
        offline_form: "blue-grey",
        missed: "red",
        closed: "grey",
      };

      return colors[status] || "grey";
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("YYYY.MM.DD. HH:mm");
    },

    async fetchSources() {
      try {
        const response = await ChatService.getSources();
        this.sources = response.data.sources || [];
      } catch (error) {
        this.errorMessage = "Unable to retrieve the source sites.";
      }
    },

    async refreshHistory() {
      if (typeof this.fetchSources === "function") {
        await this.fetchSources();
      }

      if (typeof this.fetchHistory === "function") {
        await this.fetchHistory();
      }
    },

    getHistoryParams() {
      if (!this.selectedSource) {
        return {};
      }
    
      return {
        source_site: this.selectedSource,
      };
    },
  },
};
</script>

<style>
.chat-history-page {
  padding: 0;
}

.chat-history-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.chat-history-kicker {
  margin-bottom: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .08em;
  line-height: 1.2;
  text-transform: uppercase;
}

.chat-history-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 900;
}

.chat-history-source-tabs {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 10px 26px rgba(15, 23, 42, .045);
}

.chat-history-source-tabs .v-tab {
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.chat-history-filter-info {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 8px 13px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #334155;
  font-size: 13px;
}

.chat-history-card {
  border-radius: 20px !important;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .055);
  overflow: hidden;
}

.chat-history-table {
  font-size: 14px;
}

.chat-history-table th {
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.chat-history-table td {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  font-size: 14px !important;
}

.chat-history-table tbody tr {
  cursor: pointer;
  transition: background-color .15s ease;
}

.chat-history-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.chat-history-customer-cell {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 260px;
}

.chat-history-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 14px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 900;
}

.chat-history-avatar-large {
  width: 50px;
  height: 50px;
  border-radius: 18px;
  font-size: 15px;
}

.chat-history-customer-main {
  min-width: 0;
}

.chat-history-customer-name {
  display: flex;
  align-items: center;
  color: #0f172a;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.35;
}

.chat-history-customer-contact,
.chat-history-date,
.chat-history-operator {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

.chat-history-source-cell {
  max-width: 210px;
}

.chat-last-message {
  display: inline-block;
  max-width: 380px;
  overflow: hidden;
  color: #334155;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-history-modal {
  border-radius: 20px !important;
  overflow: hidden;
}

.chat-history-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px;
  background: linear-gradient(135deg, #ffffff, #f8fafc);
}

.chat-history-modal-main {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}

.chat-history-modal-title {
  color: #0f172a;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 900;
}

.chat-history-modal-subtitle {
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.chat-history-modal-body {
  padding: 18px 22px 22px !important;
  background: #ffffff;
}

.chat-history-detail-card {
  height: 100%;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
}

.chat-history-detail-title {
  margin-bottom: 12px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
}

.chat-history-detail-row {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid #f1f5f9;
}

.chat-history-detail-row:first-of-type {
  border-top: 0;
}

.chat-history-detail-row span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.chat-history-detail-row strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.35;
  word-break: break-word;
}

.chat-history-url-row strong {
  word-break: break-all;
}

.history-chat-messages {
  max-height: 560px;
  overflow-y: auto;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background:
    radial-gradient(circle at top left, rgba(37, 99, 235, .06), transparent 34%),
    linear-gradient(180deg, #ffffff, #fbfdff);
}

.history-chat-message-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 14px;
}

.history-chat-message-row-operator {
  justify-content: flex-end;
}

.history-chat-message-row-system {
  justify-content: center;
}

.history-chat-bubble {
  max-width: 76%;
  padding: 12px 14px;
  border-radius: 18px;
  white-space: pre-line;
  box-shadow: 0 10px 22px rgba(15, 23, 42, .055);
}

.history-chat-bubble-customer {
  background: #f1f5f9;
  color: #0f172a;
  border-bottom-left-radius: 7px;
}

.history-chat-bubble-operator {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  border-bottom-right-radius: 7px;
}

.history-chat-bubble-system {
  max-width: 86%;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  text-align: center;
  box-shadow: none;
}

.history-chat-message-meta {
  margin-bottom: 5px;
  font-size: 11px;
  line-height: 1.35;
  opacity: .78;
  font-weight: 800;
}

.history-chat-message-text {
  font-size: 14px;
  line-height: 1.52;
  word-break: break-word;
}

.chat-history-empty-messages {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
}

@media (max-width: 767px) {
  .chat-history-header {
    flex-direction: column;
    align-items: stretch;
  }

  .chat-last-message {
    max-width: 220px;
  }

  .chat-history-modal-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .history-chat-bubble {
    max-width: 86%;
  }
}
</style>
